import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LogOut, ChevronDown, ChevronUp, Headphones, Scale, BarChart3, Calendar, Bell, BellOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAccessControl } from '../hooks/useAccessControl';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Composant Accordéon simple
const Accordion = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-sage-50 transition-colors"
      >
        <div className="flex items-center">
          {Icon && <Icon className="w-5 h-5 text-sage-500 mr-3" />}
          <span className="font-sans font-medium text-sage-700">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-sage-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-sage-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pt-2 border-t border-sand-100">
          {children}
        </div>
      )}
    </div>
  );
};

export const Aide = () => {
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const { isAccompagnement } = useAccessControl();
  const { supported, subscribed, subscribe, unsubscribe } = usePushNotifications(token);
  const [legal, setLegal] = useState('');
  const [loading, setLoading] = useState(true);
  const [checkinHour, setCheckinHour] = useState(8);
  const [notifEnabled, setNotifEnabled] = useState(true);

  useEffect(() => {
    fetchHelp();
    fetchNotifSettings();
  }, []);

  const fetchNotifSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/user/notification-settings`, { headers: { Authorization: `Bearer ${token}` } });
      setCheckinHour(res.data.checkin_hour ?? 8);
      setNotifEnabled(res.data.enabled ?? true);
    } catch {}
  };

  const saveNotifSettings = async (enabled, hour) => {
    try {
      await axios.put(`${API_URL}/user/notification-settings`, { enabled, checkin_hour: hour }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Paramètres sauvegardés');
    } catch { toast.error('Erreur'); }
  };

  const fetchHelp = async () => {
    try {
      const legalRes = await axios.get(`${API_URL}/help/legal`, { headers: { Authorization: `Bearer ${token}` } });
      setLegal(legalRes.data.content);
    } catch (error) {
      console.error('Erreur chargement aide:', error);
      toast.error('Erreur lors du chargement des contenus');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="aide-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-8">
        <h1 className="text-4xl font-serif font-light text-sage-700 mb-2" data-testid="aide-title">
          Mon compte
        </h1>
        <p className="text-sage-700/70 font-sans">{user?.email}</p>
      </div>

      {/* Bloc réservation accompagnement */}
      {isAccompagnement && (
        <div className="px-6 py-4">
          <div className="bg-gradient-to-br from-declic/10 via-white to-sage-50 rounded-2xl p-5 border-2 border-declic/30 shadow-sm">
            <div className="flex items-start gap-3">
              <Calendar className="w-6 h-6 text-declic flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sage-700 text-base mb-1">Vos 2 séances individuelles</h3>
                <p className="text-sage-600 text-sm mb-3">
                  Votre offre Accompagnement inclut 2 séances individuelles avec Lynda Bry-Bonnard, en cabinet ou en visio.
                </p>
                <a
                  href="mailto:brylynda@gmail.com?subject=Réservation%20séances%20Déclic%20RePas%20-%20Accompagnement"
                  className="inline-flex items-center gap-2 bg-declic hover:bg-declic/90 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
                  data-testid="book-sessions-btn"
                >
                  <Calendar className="w-4 h-4" />
                  Réserver mes 2 séances
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions rapides */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => navigate('/stats')}
            variant="outline"
            className="h-14 border-sage-200 text-sage-700 hover:bg-sage-50 rounded-xl flex-col py-2"
          >
            <BarChart3 className="w-5 h-5 mb-1" />
            <span className="text-xs">Mes stats</span>
          </Button>
          <Button
            onClick={() => navigate('/downloads')}
            variant="outline"
            className="h-14 border-sage-200 text-sage-700 hover:bg-sage-50 rounded-xl flex-col py-2"
          >
            <Headphones className="w-5 h-5 mb-1" />
            <span className="text-xs">Téléchargements</span>
          </Button>
        </div>
      </div>

      {/* Paramètres notifications */}
      {supported && (
        <div className="px-6 pb-4">
          <div className="bg-white rounded-xl border border-sand-200 p-4 space-y-4">
            <p className="text-sage-700 font-medium text-sm">Notifications</p>

            {/* Toggle global */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {subscribed ? <Bell className="w-5 h-5 text-declic" /> : <BellOff className="w-5 h-5 text-sage-400" />}
                <span className="text-sage-600 text-sm">Rappels quotidiens</span>
              </div>
              <Button
                onClick={async () => {
                  if (subscribed) {
                    const ok = await unsubscribe();
                    if (ok) { toast.success('Notifications désactivées'); setNotifEnabled(false); saveNotifSettings(false, checkinHour); }
                  } else {
                    const ok = await subscribe();
                    if (ok) { toast.success('Notifications activées'); setNotifEnabled(true); saveNotifSettings(true, checkinHour); }
                    else toast('Autorisez les notifications dans les paramètres de votre navigateur');
                  }
                }}
                variant={subscribed ? "outline" : "default"}
                className={subscribed ? "border-sage-300 text-sage-600 h-8 rounded-lg text-xs" : "bg-declic hover:bg-declic/90 text-white h-8 rounded-lg text-xs"}
                data-testid="push-toggle-button"
              >
                {subscribed ? 'Désactiver' : 'Activer'}
              </Button>
            </div>

            {/* Heure du check-in */}
            {subscribed && (
              <div className="flex items-center justify-between pt-2 border-t border-sage-100">
                <span className="text-sage-500 text-sm">Rappel check-in à</span>
                <select
                  value={checkinHour}
                  onChange={(e) => { const h = parseInt(e.target.value); setCheckinHour(h); saveNotifSettings(true, h); }}
                  className="border border-sage-200 rounded-lg px-3 py-1.5 text-sm text-sage-700 focus:outline-none focus:border-declic"
                  data-testid="checkin-hour-select"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
            )}

            {/* Aperçu */}
            {subscribed && (
              <div className="pt-2 border-t border-sage-100">
                <p className="text-sage-400 text-xs">
                  Rappel check-in : {String(checkinHour).padStart(2, '0')}:00 · Rappel inactivité : après 3 jours sans ouvrir l'app
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Déconnexion + Admin */}
      <div className="px-6 pb-4">
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full border-sage-200 text-sage-600 hover:bg-sage-50 h-11 rounded-xl text-sm"
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Se déconnecter
        </Button>
        {user?.is_admin && (
          <Button
            onClick={() => navigate('/admin')}
            className="w-full bg-sage-500 hover:bg-sage-600 text-white mt-2 h-11 rounded-xl text-sm"
            data-testid="admin-link-button"
          >
            Administration
          </Button>
        )}
      </div>

      {/* ====== SÉPARATION ====== */}
      <div className="h-2 bg-sage-100" />

      {/* Légal */}
      {legal && (
        <div className="px-6 py-4">
          <Accordion title="Mentions légales" icon={Scale}>
            <div className="markdown-content prose-sage">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{legal}</ReactMarkdown>
            </div>
          </Accordion>
        </div>
      )}
    </div>
  );
};

export default Aide;
